import { BoardsObserver }  from "../components/Observer";
import Board from '../models/board';
import TasksController from './TasksController';
import TodosController from './TodosController';
import NotesController from './NotesController';
import BudgetController from "./BudgetController";
import { getBoards } from "../apihelper";
import { BoardsSubject } from "../components/Subject";
import { BOARD_TYPES, EVENTS } from "../global/constants";
import HabitsController from "./HabitsController";
import LogsController from "./WLogsController";
import WorkoutsController from "./WorkoutsController";

export default class BoardsController {
    constructor(data=[]) {
        this.data = data;
        this.boardObjects = [];
        this.subject = false;
        this.observer = false;
        this.reactToEvent = this.reactToEvent.bind(this);
        this.connections = new Map();
        this.initialConnectionsMade = false;

        this.bindFunctions();

        this.createSubject();
    }

    bindFunctions() {
        this.updateBoardObjects = this.updateBoardObjects.bind(this);
        this.registerConnections = this.registerConnections.bind(this);
        this.registerConnection = this.registerConnection.bind(this);
    }

    fetchBoardsData(callback) {
        getBoards(
            (results) => {
                if ( results.boards ) {
                    this.setData(results.boards);
                    if ( callback ) {                        
                        callback(results.boards);
                    }
                }
            }
        )
    }

    reloadBoardsData() {
        this.fetchBoardsData(() => {
            this.subject.runBoardsReload();
        });
        
    }

    setData(data=[]) {
        this.data = data;
        this.updateBoardObjects();
    }

    updateBoardObjects() {
        this.boardObjects=[];
        this.data.forEach(board => {
            const newBoard = this.createBoardByClass(board);
            this.boardObjects.push(newBoard);
        });


        // connect boards here
        // ToDoController's subject (n to 1) --> TasksController's observer
        this.registerConnections();        
    }

    getBoardObjects() {
        return this.boardObjects;
    }

    getTasksBoardObject() {
        if ( this.boardObjects.length === 0 ) {
            return false;
        }

        const index = this.boardObjects.map( (e) => {  return e.type; } ).indexOf(BOARD_TYPES.tasks);

        if ( index !== -1 ) {
            const tasksCtrl = this.boardObjects[index];
            return tasksCtrl;
        }

        return false;
    }

    getBoardObjectById(id = -1) {
        if ( this.boardObjects.length === 0 || id === -1 ) {
            return false;
        }

        const index = this.boardObjects.map( (e) => {  return e.id; } ).indexOf(id);

        if ( index !== -1 ) {
            const boardObject = this.boardObjects[index];
            return boardObject;
        }


        return false;
    }

    // connect todoBoards to TasksCtrls
    // ToDoController's subject (n to 1) --> TasksController's observer
    registerConnections() {
        
        const tasksCtrl = this.getTasksBoardObject();

        if ( tasksCtrl ) {

            this.boardObjects.forEach(boardCtrl => {
                if ( boardCtrl.type === BOARD_TYPES.todo ) {
                    boardCtrl.addObserver( tasksCtrl.getObserver() );

                    const key = boardCtrl.id ? boardCtrl.id : -1;
                    this.connections.set(key, {subject: boardCtrl, observer: tasksCtrl} );
                }
            });
        }
    }

    // This fn might not be needed
    registerConnection(boardDetails = {}) {

        if ( boardDetails.type === BOARD_TYPES.tasks ) {
            const tasksCtrl = this.getTasksBoardObject();

            if ( tasksCtrl ) {
                this.boardObjects.forEach(boardCtrl => {
                    if ( boardCtrl.type === BOARD_TYPES.todo ) {
                        boardCtrl.addObserver( tasksCtrl.getObserver() );

                        const key = boardCtrl.id ? boardCtrl.id : -1;
                        this.connections.set(key, {subject: boardCtrl, observer: tasksCtrl} );
                    }
                });
            }
        } else if ( boardDetails.type === BOARD_TYPES.todo ) {
            const tasksCtrl = this.getTasksBoardObject();
            const boardCtrl = this.getBoardObjectById(boardDetails.id ? boardDetails.id : -1);
        
            if ( tasksCtrl && boardCtrl ) {
                boardCtrl.addObserver( tasksCtrl.getObserver() );
            }
        }
    }

    // Unregisters connections between TaskBoard and ToDosBoard
    // if boardDetails is TasksBoard, 
    //      all the connections are cut
    // if boardDetails is ToDo, 
    //      specific connection is removed
    unregisterConnection(boardDetails = {}) {
        const numberOfConnections = this.connections.size;
        if ( numberOfConnections ) {
            return false;
        } 
        if ( boardDetails !== {} ) {
            if ( boardDetails.type === BOARD_TYPES.tasks ) {
                
                // Any way to minimize this?
                const tasksCtrl = this.getTasksBoardObject();
                if ( tasksCtrl ) {
                    
                    const boardCtrlIds = [];
                    this.connections.forEach(connection => {
                        const boardCtrl = connection.subject;
                        const boardId = boardCtrl.id;

                        boardCtrl.removeObserver( tasksCtrl.getObserver() );
                        boardCtrlIds.push(boardId);
                    });

                    boardCtrlIds.forEach(key => this.connections.delete(key));
                }
            } else if ( boardDetails.type === BOARD_TYPES.todo ) {
                const boardId = boardDetails.id;
                const connection = this.connections.get(boardId);

                if ( connection ) { 
                    const boardCtrl = connection.subject;
                    const tasks = connection.observer;

                    if ( boardCtrl && tasks ) { 
                        const tasksObserver = tasks.getObserver();
                        if ( tasksObserver ) {
                            boardCtrl.removeObserver( tasks.getObserver() );
                        }
                    }

                    this.connections.delete(boardId);
                }
            }
        }
    }

    // Reacts to events
    // if board visibility is changed
    // if board visibility or board order is changed
    // Reloads boards data which 
    //      gets new data and updates boardObjects
    reactToEvent(event) {
        if ( event.name === EVENTS.update ) {
            const value = event.value;

            if ( value === EVENTS.boardVisibility || value === EVENTS.boardOrder || value === EVENTS.boardEdit || value === EVENTS.boardNew ) {
                this.reloadBoardsData();
            }

            if  ( value === EVENTS.boardVisibility ) {
                if ( event.hidden ) {
                    const boardDetails = event.boardDetails;
                    const type = boardDetails.type;
                    const numberOfConnections = this.connections.size;

                    if ( numberOfConnections !== 0 && (type === BOARD_TYPES.tasks || type === BOARD_TYPES.todo) ) { 
                        this.unregisterConnection( event.boardDetails );
                    }
                }
            }
        }  
    }

    createObserver() {
        this.observer = new BoardsObserver(this.reactToEvent);
    }

    getObserver() {
        return this.observer;
    }

    createSubject() {
        this.subject = new BoardsSubject()   
    }

    getSubject() {
        return this.subject;
    }

    addObserver(observer=false) {
        if ( observer ) {
            this.subject.addObserver(observer);
        }
    }

    createBoardByClass(boardData={}) {
        let boardInstance;
        switch(boardData.type) {
            case BOARD_TYPES.notes:
                boardInstance = new NotesController();
                break;
			case BOARD_TYPES.todo:
				boardInstance = new TodosController();
				break;
			case BOARD_TYPES.tasks:
				boardInstance = new TasksController();
                break;
            case BOARD_TYPES.habits:
                boardInstance = new HabitsController();
                break;
            case BOARD_TYPES.wlogs:
                boardInstance = new LogsController();
                break;
            case BOARD_TYPES.workouts:
                boardInstance = new WorkoutsController();
                break;
            case BOARD_TYPES.budget:
                boardInstance = new BudgetController();
                break;
			default: 
                boardInstance = new Board();
				break;
        }

        boardInstance.initFromData(boardData);
        boardInstance.fetchData();

        return boardInstance;
    }
}